import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Box, Paper, Typography } from '@mui/material';
import { connect } from 'react-redux';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

import { DeepsightComponentLoader } from '@commons/DeepsightComponents';
import { Dropdown } from '@commons/utils/styledLibraryComponents';
import { getClientInfo } from '@selectors/client';
import { getTheme } from '@commons/utils/theme';
import { showErrorMessage } from '@actions/messageconfirmation';

import { fetchAllProductsForStores } from './utils/fetch';
import { getMetricOptions } from './utils/config';
import ChartTooltip from '../common/ChartTooltip';
import RestaurantLimitedDropdown from '../common/RestaurantLimitedDropdown';

const ProductComparisonChart = ({
  selectedStores,
  currentPeriod,
  previousPeriod,
  productTableData,
  showErrorMessage,
  isLoading,
  client: { storeName },
}) => {
  const metricOptions = getMetricOptions();
  const [comparisonChartData, setComparisonChartData] = useState([]);
  const [isLoadingComparison, setIsLoadingComparison] = useState(false);
  const [allComparisonData, setAllComparisonData] = useState({});
  const [selectedProductForComparison, setSelectedProductForComparison] = useState([]);
  const [selectedStoresForComparison, setSelectedStoresForComparison] = useState([]);
  const [selectedMetricForComparison, setSelectedMetricForComparison] = useState(metricOptions[1]);

  const theme = getTheme();

  const fetchAllComparisonData = async () => {
    setIsLoadingComparison(true);
    try {
      const comparisonData = await fetchAllProductsForStores(
        selectedStores,
        currentPeriod,
        previousPeriod,
      );

      setAllComparisonData(comparisonData);
    } catch (error) {
      setAllComparisonData({});
      showErrorMessage(i18next.t('GENERAL.GLOBAL_FETCH_ERROR'));
    } finally {
      setIsLoadingComparison(false);
    }
  };

  // Initialize comparison chart states
  useEffect(() => {
    if (productTableData && productTableData.length > 0 && !selectedProductForComparison.length) {
      const topProduct = productTableData.reduce((prev, current) =>
        (prev.turnoverExcludingTaxes?.current || 0) > (current.turnoverExcludingTaxes?.current || 0)
          ? prev
          : current,
      );
      setSelectedProductForComparison(topProduct);
    }
  }, [productTableData]);

  // Fetch all comparison data when stores or dates change
  useEffect(() => {
    if (
      currentPeriod.startDate &&
      currentPeriod.endDate &&
      previousPeriod.startDate &&
      previousPeriod.endDate &&
      selectedStores.length &&
      !isLoadingComparison
    )
      fetchAllComparisonData();
  }, [
    selectedStores,
    currentPeriod.startDate,
    currentPeriod.endDate,
    previousPeriod.startDate,
    previousPeriod.endDate,
  ]);

  // Filter data based on selected product and metric
  useEffect(() => {
    if (!selectedProductForComparison || !Object.keys(allComparisonData).length) {
      setComparisonChartData([]);
      return;
    }

    const chartData = selectedStoresForComparison.map((store) => {
      const storeData = allComparisonData[store.id];
      const productData = storeData?.products?.[selectedProductForComparison.id];

      if (!productData) {
        return {
          name: store.name,
          previous: 0,
          current: 0,
        };
      }

      return {
        name: store.name,
        previous: productData[selectedMetricForComparison.id]?.previous || 0,
        current: productData[selectedMetricForComparison.id]?.current || 0,
      };
    });

    setComparisonChartData(chartData);
  }, [
    selectedProductForComparison,
    selectedMetricForComparison,
    selectedStoresForComparison,
    allComparisonData,
  ]);

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, height: 420, border: '1px solid #E0E0E0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 420 }}>
          <DeepsightComponentLoader />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, height: 420, border: '1px solid #E0E0E0', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {i18next.t('ANALYSIS.PERFORMANCE.PRODUCT_COMPARISON_CHART_TITLE', { storeName })}
        </Typography>

        {/* Filtres */}
        <Box sx={{ display: 'flex', gap: 2, '#dropdown-list': { maxHeight: '200px' } }}>
          {/* Dropdown Restaurants */}
          <RestaurantLimitedDropdown
            selectedStores={selectedStoresForComparison}
            setSelectedStores={setSelectedStoresForComparison}
            stores={selectedStores}
          />
          {/* Dropdown Produit */}
          <Dropdown
            customStyle={{
              width: '300px',
            }}
            isRequired={true}
            isUniqueSelection={true}
            items={productTableData || []}
            label={i18next.t('GENERAL.PRODUCT')}
            searchPlaceholder={i18next.t('GENERAL.SEARCH')}
            selectedItem={selectedProductForComparison}
            onSelectionChange={setSelectedProductForComparison}
          />
          {/* Dropdown Métrique */}
          <Dropdown
            customStyle={{
              width: '200px',
            }}
            isRequired={true}
            isSearchDisabled={true}
            isUniqueSelection={true}
            items={metricOptions || []}
            label={i18next.t('ANALYSIS.PERFORMANCE.METRIC_DROPDOWN_LABEL')}
            selectedItem={selectedMetricForComparison}
            onSelectionChange={setSelectedMetricForComparison}
          />
        </Box>
      </Box>

      {/* Graphique */}
      {isLoadingComparison ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 420 }}>
          <DeepsightComponentLoader />
        </Box>
      ) : comparisonChartData.length > 0 ? (
        <ResponsiveContainer height={300} width="100%">
          <BarChart
            data={comparisonChartData}
            isLoading={isLoadingComparison}
            margin={{ bottom: 5, left: 20, right: 30, top: 20 }}
          >
            <CartesianGrid stroke="#EDF0F6" strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fill: '#6B768C', fontSize: 12 }} tickLine={false} />
            <YAxis axisLine={false} tick={{ fill: '#6B768C', fontSize: 12 }} tickLine={false} />
            <Tooltip
              content={
                <ChartTooltip
                  options={metricOptions}
                  selectedMetric={selectedMetricForComparison}
                />
              }
              cursor={{ fill: 'rgba(0, 0, 0, 0.03)' }}
            />
            <Legend />

            {/* Barre de référence (gris) */}
            <Bar
              dataKey="previous"
              fill={theme.colors.greys.medium}
              name={i18next.t('ANALYSIS.PERFORMANCE.PREVIOUS_PERIOD')}
              radius={[2, 2, 0, 0]}
            />

            {/* Barre actuelle (bleu Inpulse) */}
            <Bar
              dataKey="current"
              fill={theme.colors.blue}
              name={i18next.t('ANALYSIS.PERFORMANCE.CURRENT_PERIOD')}
              radius={[2, 2, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <Typography color="textSecondary" variant="body2">
            {i18next.t('GENERAL.NO_DATA_TO_DISPLAY')}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
});

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductComparisonChart);
